import { useEffect, useState } from "react";
import { Search, Tag as TagIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AutomationFunnelStageSelector } from "@/components/automations/nodes/AutomationFunnelStageSelector";
import { supabase } from "@/integrations/supabase/client";
import type { DisparoLeadFilters } from "@/lib/disparos-types";

interface TagOption {
  id: string;
  name: string;
  color: string;
}

interface DisparoLeadFiltersPanelProps {
  workspaceId: string;
  filters: DisparoLeadFilters;
  onChange: (filters: DisparoLeadFilters) => void;
}

function countActiveFilters(filters: DisparoLeadFilters): number {
  let count = 0;
  if (filters.funnelId) count += 1;
  if (filters.stageSlug) count += 1;
  if (filters.tagIds.length > 0) count += 1;
  if (filters.search.trim()) count += 1;
  return count;
}

export function DisparoLeadFiltersPanel({
  workspaceId,
  filters,
  onChange,
}: DisparoLeadFiltersPanelProps) {
  const [tags, setTags] = useState<TagOption[]>([]);
  const [tagOpen, setTagOpen] = useState(false);
  const activeFilterCount = countActiveFilters(filters);

  useEffect(() => {
    if (!workspaceId) return;
    supabase
      .from("lead_tags")
      .select("id, name, color")
      .eq("workspace_id", workspaceId)
      .order("name")
      .then(({ data }) => setTags(data || []));
  }, [workspaceId]);

  const toggleTag = (tagId: string) => {
    const next = filters.tagIds.includes(tagId)
      ? filters.tagIds.filter((id) => id !== tagId)
      : [...filters.tagIds, tagId];
    onChange({ ...filters, tagIds: next });
  };

  const handleFunnelStage = (data: {
    funnel_id: string;
    funnel_name: string;
    stage_slug: string;
    stage_name: string;
  }) => {
    onChange({
      ...filters,
      funnelId: data.funnel_id,
      funnelName: data.funnel_name,
      stageSlug: data.stage_slug,
      stageName: data.stage_name,
    });
  };

  const handleFunnelOnly = (data: { funnel_id: string; funnel_name: string }) => {
    onChange({
      ...filters,
      funnelId: data.funnel_id,
      funnelName: data.funnel_name,
      stageSlug: null,
      stageName: null,
    });
  };

  const clearFunnelStage = () => {
    onChange({
      ...filters,
      funnelId: null,
      funnelName: null,
      stageSlug: null,
      stageName: null,
    });
  };

  const showAllWithPhone = () => {
    onChange({
      funnelId: null,
      funnelName: null,
      stageSlug: null,
      stageName: null,
      tagIds: [],
      search: "",
    });
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground leading-relaxed">
        Leads com telefone válido.
        {activeFilterCount > 0 && (
          <>
            {" "}
            <button
              type="button"
              className="text-foreground underline-offset-2 hover:underline"
              onClick={showAllWithPhone}
            >
              Limpar filtros
            </button>
          </>
        )}
      </p>

      <div className="space-y-1">
        <Label className="text-xs font-normal text-muted-foreground">Buscar</Label>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Nome ou telefone..."
            value={filters.search}
            onChange={(e) => onChange({ ...filters, search: e.target.value })}
            className="h-8 pl-8 text-sm"
          />
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs font-normal text-muted-foreground">Funil</Label>
          {(filters.funnelId || filters.stageSlug) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-xs text-muted-foreground"
              onClick={clearFunnelStage}
            >
              Limpar
            </Button>
          )}
        </div>
        <AutomationFunnelStageSelector
          workspaceId={workspaceId}
          selectedFunnelId={filters.funnelId ?? undefined}
          selectedFunnelName={filters.funnelName ?? undefined}
          selectedStageId={filters.stageSlug ?? undefined}
          selectedStageName={filters.stageName ?? undefined}
          showFunnelLabel="Funil"
          showStageLabel="Coluna (opcional)"
          allStagesLabel="Todas as colunas"
          stageOptional
          onFunnelOnlySelect={handleFunnelOnly}
          onSelect={handleFunnelStage}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs font-normal text-muted-foreground">Tags</Label>
        <Popover open={tagOpen} onOpenChange={setTagOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-full justify-start gap-2 font-normal"
            >
              <TagIcon className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
              {filters.tagIds.length > 0 ? (
                <span className="truncate text-sm">
                  {filters.tagIds.length === 1
                    ? tags.find((t) => t.id === filters.tagIds[0])?.name ?? "1 tag"
                    : `${filters.tagIds.length} tags`}
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">Filtrar por tags...</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-2" align="start">
            {tags.length === 0 ? (
              <p className="text-sm text-muted-foreground px-2 py-1">
                Nenhuma tag cadastrada. Crie tags na página de Leads para filtrar aqui.
              </p>
            ) : (
              <div className="space-y-0.5 max-h-48 overflow-y-auto">
                {tags.map((tag) => (
                  <label
                    key={tag.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer text-sm"
                  >
                    <Checkbox
                      checked={filters.tagIds.includes(tag.id)}
                      onCheckedChange={() => toggleTag(tag.id)}
                    />
                    <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${tag.color}`} />
                    <span className="truncate">{tag.name}</span>
                  </label>
                ))}
              </div>
            )}
            {filters.tagIds.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-2 h-7 text-xs"
                onClick={() => onChange({ ...filters, tagIds: [] })}
              >
                Limpar tags
              </Button>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
